/**
 * contractFormat.js — 계약 상품 문자열 파싱/표시 공유 유틸리티
 *
 * ── DB 저장 형식 (ticket_type) ────────────────────────────────────────────────
 *   신형: "상품명(N개월)×장수, 상품명×장수"
 *   구형: "카테고리명×장수"  또는  "카테고리명" (장수는 ticket_count에 별도 저장)
 *
 * ── 표시 형식 ────────────────────────────────────────────────────────────────
 *   "상품명×장수 | 상품명×장수"
 *   유효기간(N개월) 제거, 복수 상품은 " | " 구분, 구/신 통일
 *
 * ── 정산 구조체 ──────────────────────────────────────────────────────────────
 *   parseContractProducts() → [{ name, validity, count }, ...]
 *   - name    : 상품명 (string)
 *   - validity: 유효기간(개월, number) — 없으면 null
 *   - count   : 장수 (number) — 없으면 null
 *   이 구조체를 기반으로 상담사/마케팅담당자 정산서를 생성합니다.
 */

// "상품명(N개월)" → { name: '상품명', validity: N }
function extractName(rawName) {
  const m = String(rawName || '').match(/^(.*?)\((\d+)개월\)$/);
  if (m) return { name: m[1].trim(), validity: Number(m[2]) };
  return { name: String(rawName || '').trim(), validity: null };
}

/**
 * ticket_type + ticket_count → 정산용 상품 배열
 *
 * @param {string|null}  ticketType  - DB ticket_type 컬럼값
 * @param {number|null}  ticketCount - DB ticket_count 컬럼값 (구형 단일 장수)
 * @returns {Array<{ name: string, validity: number|null, count: number|null }>}
 *
 * 사용 예시 (정산서 집계):
 *   const products = parseContractProducts(row.ticket_type, row.ticket_count);
 *   products.forEach(({ name, validity, count }) => { ... });
 */
export function parseContractProducts(ticketType, ticketCount) {
  const str = String(ticketType || '').trim();
  if (!str) return [];

  // 신형/구형 모두 "×" 포함 → "상품명(N개월)×장수" 또는 "상품명×장수"
  if (str.includes('×')) {
    return str.split(', ').flatMap((part) => {
      const idx = part.indexOf('×');
      const rawName = part.slice(0, idx).trim();
      const cntStr = part.slice(idx + 1).trim();
      const { name, validity } = extractName(rawName);
      if (!name) return [];
      return [{ name, validity, count: cntStr !== '' ? Number(cntStr) : null }];
    });
  }

  // 구형: ticket_type에 장수 없이 상품명만 저장, 장수는 ticket_count에 별도
  const { name, validity } = extractName(str);
  const count =
    ticketCount !== null && ticketCount !== undefined && ticketCount !== ''
      ? Number(ticketCount)
      : null;
  return [{ name, validity, count }];
}

/**
 * ticket_type + ticket_count → 배지/목록 표시용 문자열
 * ※ 구형(문자열) 데이터 표시 및 이력 로그 호환용 — 신규 코드는 formatItemsDisplay 사용
 *
 * @returns {string}  예) "동반할인권×500 | 여행권(저)×100"
 */
export function formatContractDisplay(ticketType, ticketCount) {
  const products = parseContractProducts(ticketType, ticketCount);
  if (!products.length) return '-';
  return products
    .map((p) => (p.count != null ? `${p.name}×${p.count}` : p.name))
    .join(' | ');
}

/**
 * contract_items 배열 → 표시용 문자열
 * API가 반환하는 [{ticketTypeName, quantity, isService, validityMonths?}] 형식 대응
 *
 * @param {Array}  items      — contract_items 배열
 * @param {object} [opts]
 * @param {boolean} [opts.serviceOnly] — true: 서비스 항목만, false/undefined: 주계약만
 * @returns {string}  예) "동반할인권×500 · 여행권×100"  (없으면 '-')
 */
export function formatItemsDisplay(items, opts = {}) {
  const arr = (items || []).filter((i) =>
    opts.serviceOnly ? Boolean(i.isService) : !i.isService
  );
  if (!arr.length) return '-';
  return arr.map((i) => `${i.ticketTypeName || '?'}×${i.quantity}`).join(' · ');
}
